import { FunctionalComponent, RefObject, createContext, h } from 'preact';
import { TabId, UseTabsReturn } from './interface';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';
import { SCROLL_CONTAINER_ID } from '../../constants';
import { useUiContext } from '../../contexts/UiContext';

const initialValue: UseTabsReturn = {
  panelRefs: [],
  panels: null,
  registerPanel: () => null,
  registerRef: () => null,
  select: () => null,
  selected: null,
  unRegisterPanel: () => null,
};

const TabsContext = createContext<UseTabsReturn>(initialValue);

export const Tabs: FunctionalComponent = ({ children }) => {
  const { tabView, setTabView } = useUiContext();
  const [selected, setSelectedTab] = useState<TabId | null>(tabView);
  const [panels, setPanels] = useState<TabId[]>([]);
  const [scrollElement, setScrollElement] = useState<Element | null | undefined>();
  const [panelRefs, setPanelRefs] = useState<RefObject<HTMLButtonElement>[]>([]);

  const resetScroll = useCallback(() => {
    setScrollElement(document.activeElement?.shadowRoot?.querySelector(`[data-testid=${SCROLL_CONTAINER_ID}]`));

    if (scrollElement) {
      scrollElement.scrollTop = 0;
    }
  }, [scrollElement]);

  useEffect(() => {
    if (!selected || selected !== tabView) {
      setSelectedTab(tabView);
    }
  }, [selected, tabView]);

  const select = (tabId: TabId) => {
    setSelectedTab(tabId);
    setTabView(tabId);
    resetScroll();
  };

  const registerPanel = (panel: TabId): void => {
    setPanels((prevState) => [...prevState, panel]);
  };

  const unRegisterPanel = (panel: TabId): void => {
    setPanels(panels.filter((item) => item !== panel));
  };

  const registerRef = (ref: RefObject<HTMLButtonElement>): void => {
    setPanelRefs((prevState) => [...prevState, ref]);
  };

  return (
    <TabsContext.Provider value={{ panelRefs, panels, registerPanel, registerRef, select, selected, unRegisterPanel }}>
      {children}
    </TabsContext.Provider>
  );
};

export const useTabs = (): UseTabsReturn => {
  return useContext(TabsContext);
};
